

import { useState, useCallback } from 'react';
import { getLatestVersion, detectPlatform, downloadFile, formatFileSize } from '@/api/version';
import { AppVersion } from '@/types/version';
import { useToast } from '@/hooks/use-toast';

interface UseDownloadReturn {
  isLoading: boolean;
  error: string | null;
  latestVersion: AppVersion | null;
  downloadLatest: () => Promise<void>;
  getVersionInfo: () => Promise<AppVersion | null>;
}

/**
 * 下载功能 Hook
 * 提供获取最新版本和下载功能
 */
export function useDownload(): UseDownloadReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latestVersion, setLatestVersion] = useState<AppVersion | null>(null);
  const { toast } = useToast();

  /**
   * 获取版本信息
   */
  const getVersionInfo = useCallback(async (): Promise<AppVersion | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const platform = detectPlatform();
      const version = await getLatestVersion(platform);
      setLatestVersion(version);
      return version;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '获取版本信息失败';
      setError(errorMessage);
      console.error('获取版本信息失败:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * 下载最新版本
   */
  const downloadLatest = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // 如果还没有版本信息，先获取
      let version = latestVersion;
      if (!version) {
        version = await getVersionInfo();
      }

      if (!version) {
        throw new Error('无法获取版本信息');
      }

      if (!version.downloadUrl) {
        throw new Error('下载链接不可用');
      }

      // 显示下载提示
      toast({
        title: '开始下载',
        description: `正在下载 Estha ${version.version} (${formatFileSize(version.fileSize)})`,
      });

      // 生成文件名
      const filename = `Estha-${version.version}-${version.platform}.dmg`;

      // 触发下载
      downloadFile(version.downloadUrl, filename);

      // 显示成功提示
      toast({
        title: '下载已开始',
        description: '请查看浏览器下载进度',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '下载失败';
      setError(errorMessage);

      toast({
        title: '下载失败',
        description: errorMessage,
        variant: 'destructive',
      });

      console.error('下载失败:', err);
    } finally {
      setIsLoading(false);
    }
  }, [latestVersion, getVersionInfo, toast]);

  return {
    isLoading,
    error,
    latestVersion,
    downloadLatest,
    getVersionInfo,
  };
}
